import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment-timezone';
import { useFormContext } from 'react-hook-form';
import { fetchOptions } from '../queries';
import Select from '../select/Select';
import { getUserRegion, barcodeLastFourDigits } from '../../../../../utils';

function useFirstRender() {
    const ref = useRef(true);
    const firstRender = ref.current;
    ref.current = false;
    return firstRender;
}

const Pickup = ({ name, query, props, user, intervalRef }) => {
    const [selectProps, setSelectProps] = useState({ ...props, options: [] });
    const [lockOptions, setLockOptions] = useState(false);
    const [loading, setLoading] = useState(true);
    const isFirstRender = useFirstRender();
    const {
        formState: { errors, dirtyFields },
        watch,
        setError,
        clearErrors,
        getValues,
        setValue,
        selectedValues,
        updateSelectedValues,
        // wrap
    } = useFormContext();
    const { endpoint, where = {}, order, populate, labelKey, roleConstraints, useLoopback } = query;
    const { intervalField, intervalFilterKey, referenceField, referenceKey, filterKey, autoSelect } = intervalRef || {};

    useEffect(
        () => {
            const reference = !!referenceField && selectedValues[referenceField];
            if (!reference || typeof reference === 'string') {
                return;
            }

            const referenceValue = reference?.[referenceKey];

            const whereClause = { ...where };

            if (referenceValue) {
                // extend where object for filtering options
                const whereValue = Array.isArray(referenceValue) ? referenceValue.join(',') : referenceValue;
                whereClause[filterKey] = whereValue;
            }

            const interval = getValues(intervalField);
            whereClause[intervalFilterKey] = { between: [moment(interval).subtract(5, 'days'), moment(interval).add(5, 'days')] };

            const value = getValues(name);

            const getOptions = async () => {
                setLoading(true);
                const optionsResponse = await fetchOptions({ endpoint, where: whereClause, order, populate, labelKey, roleConstraints, user, useLoopback });

                if (optionsResponse.error) {
                    setSelectProps((prev) => ({ ...prev, options: [] }));
                    setError(name, { type: 'fetch options', message: `Could not fetch ${props.label} options` });
                } else {
                    setSelectProps((prev) => ({ ...prev, options: optionsResponse }));
                    clearErrors([name]);
                }
                setLoading(false);
            };

            if (!lockOptions) {
                getOptions();
            }
        },
        !!intervalField && !!referenceField ? [...watch([intervalField, referenceField]), user] : [user]
    );

    const getLabMatchingStatus = (pickup) => {
        if (pickup.lab_report_ids && pickup.lab_report_ids.length > 0) {
            const lab = pickup.lab_report_ids[0];
            if (lab.manufactured_lab_result) {
                return 'Manufactured';
            }

            return 'True Lab';
        }

        return 'No Lab';
    };

    // Form label for select options and selected display
    const formatLabel = (option, options) => {
        const region = getUserRegion();
        let labelOption = { ...option };
        if (typeof option === 'string') {
            labelOption = options?.find((opt) => (opt.id || opt._id) === option);
        }

        if (!labelOption || !Object.keys(labelOption).length) {
            return '';
        }
        const { sample_barcodes, tank_number, volume, created_at, manifest_number, lt_number, component_barcode } = labelOption;

        switch (region) {
            case 'CDI':
            case 'CACIQUE':
                return `Date: ${moment(created_at).format('YYYY/MM/DD')} - Manifest: ${manifest_number} - Tank: ${tank_number} - Amount: ${volume}lbs`;
            case 'UDA':
                return `Date: ${moment(created_at).format('YYYY/MM/DD')} - Invoice: ${lt_number} - Tank: ${tank_number} - Amount: ${volume}lbs - ${getLabMatchingStatus(labelOption)}`;
            case 'BORDEN':
                return `Date: ${moment(created_at).format('YYYY/MM/DD')} - Amount: ${volume}lbs - Ticket Number: ${component_barcode}`;
            case 'PEI':
                return `Date: ${moment(created_at).format('YYYY/MM/DD')} - Volume: ${volume}`;
            case 'PRAIRIE':
            case 'LEGACY':
                return `Date: ${moment(created_at).format('YYYY/MM/DD')} ${sample_barcodes?.[0] ? `- Barcode: ${sample_barcodes}` : ''} - Tank: ${tank_number} - Amount: ${volume}lbs`;
            case 'MMPA':
                return `Date: ${moment(created_at).format('YYYY/MM/DD')} ${sample_barcodes?.[0] ? `- Barcode: ${barcodeLastFourDigits(sample_barcodes[0])}` : ''} - Tank: ${tank_number} - Amount: ${volume}lbs`;
            case 'TCJ-NEBRASKA':
            case 'TCJ-ERIE':
            case 'TCJ-WHITEEAGLE':
            case 'TCJ-GPDA':
            case 'TCJ-LIBERTY':
            case 'TCJ-KYTN':
                return `Date: ${moment(created_at).format('YYYY/MM/DD')} ${sample_barcodes?.[0] ? `- Barcode: ${sample_barcodes}` : ''} - Tank: ${tank_number} - Amount: ${volume}lbs`;
            case 'UNC':
                return `Date: ${moment(created_at).format('YYYY/MM/DD')} - Volume: ${volume} - Barcode: ${sample_barcodes}`;
            default:
                return `Date: ${moment(created_at).format('YYYY/MM/DD')} - Volume: ${volume} - Barcode: ${sample_barcodes}`;
        }
    };

    return (
        <Select
            name={name}
            formatLabel={formatLabel}
            props={{ ...selectProps }}
            loading={loading}
            // wrap
        />
    );
};

export default Pickup;
